import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material";

const buttonSx = { fontFamily: "inherit", textTransform: "none", minHeight: 44, color: "var(--iio-info)", borderColor: "var(--iio-border)" };
const labelOf = choice => `${choice.content.fullName} → ${choice.actions.fullName} (${choice.workflow.split("/").pop()}${choice.actionsRef ? ` · ${choice.actionsRef.split("@").at(-1)}` : ""})`;
const errorText = (error, t) => ["AbortError", "TimeoutError"].includes(error?.name)
  ? t("repositoryDiscovery.timeout") : error?.message || t("repositoryIdentity.failed");

export default function RepositoryAuswahl({ api, spaceId, owner, connectionRevision, onSaved, onReload }) {
  const { t } = useTranslation("cicdsetup");
  const refs = useRef({ t, onSaved, onReload }); refs.current = { t, onSaved, onReload };
  const generation = useRef(0), saving = useRef(false);
  const [attempt, setAttempt] = useState(0), [state, setState] = useState({ loading: true }), [choiceId, setChoiceId] = useState("");
  useEffect(() => {
    const version = ++generation.current;
    saving.current = false; setChoiceId(""); setState({ loading: true });
    (async () => {
      try {
        const result = await api(`/api/cicd/repository-discovery?spaceId=${encodeURIComponent(spaceId)}&owner=${encodeURIComponent(owner)}`, { timeout: 180000, retry: false });
        if (version !== generation.current) return;
        if (result?.complete !== true || result.bindingEnabled !== true || !result.selectionRevision || !Array.isArray(result.choices)
            || result.choices.some(choice => !choice.id || !choice.content?.fullName || !choice.actions?.fullName || !choice.workflow))
          throw new Error(refs.current.t("repositoryIdentity.invalidResponse"));
        setState({ result });
        if (result.choices.length === 1) setChoiceId(result.choices[0].id);
      } catch (error) {
        if (version === generation.current) setState({ error: errorText(error, refs.current.t) });
      }
    })();
    return () => { generation.current++; };
  }, [api, spaceId, owner, connectionRevision, attempt]);
  async function confirm() {
    if (saving.current || !state.result?.choices.some(choice => choice.id === choiceId)) return;
    saving.current = true;
    const version = generation.current;
    setState(previous => ({ ...previous, saving: true }));
    try {
      const response = await api("/api/cicd/repository-discovery", { method: "POST", timeout: 180000, retry: false, body: JSON.stringify({
        spaceId, owner, selectionId: choiceId, expectedRevision: state.result.selectionRevision,
      }) });
      if (version !== generation.current) return;
      if (!response?.ok || response.spaceId !== spaceId) throw new Error(refs.current.t("repositoryIdentity.invalidResponse"));
      setState({ saved: true }); refs.current.onSaved?.(response);
    } catch (error) {
      // An unknown result must be read again. Do not offer a blind mutation retry.
      if (version === generation.current) setState({ error: errorText(error, refs.current.t), reloadRequired: true });
    } finally { if (version === generation.current) saving.current = false; }
  }
  return <Box sx={{ minWidth: 0, color: "var(--iio-text-1)", fontFamily: "inherit" }}>
    <Typography component="p" sx={{ fontFamily: "inherit", mb: 2 }}>{t("repositoryDiscovery.explanation")}</Typography>
    {(state.loading || state.saving) && <Stack direction="row" spacing={1} alignItems="center" role="status" aria-busy="true">
      <CircularProgress size={18} sx={{ color: "var(--iio-info)" }} aria-hidden="true"/>
      <span>{t(state.saving ? "repositoryIdentity.saving" : "repositoryIdentity.loading")}</span>
    </Stack>}
    {state.error && <Alert severity="error" sx={{ fontFamily: "inherit", color: "var(--iio-text-1)", background: "var(--iio-bg-2)", mb: 2 }}>{state.error}</Alert>}
    {state.saved && <Typography role="status" sx={{ fontFamily: "inherit" }}>{t("repositoryDiscovery.saved")}</Typography>}
    {state.result && !state.saving && <>
      {!state.result.choices.length ? <Typography role="status" sx={{ fontFamily: "inherit" }}>{t("repositoryDiscovery.empty")}</Typography> : <>
        <FormControl fullWidth sx={{ minWidth: 0, mb: 2,
          "& .MuiInputLabel-root, & .MuiSelect-icon": { color: "var(--iio-text-2)" },
          "& .MuiOutlinedInput-root": { fontFamily: "inherit", color: "var(--iio-text-1)", background: "var(--iio-bg-1)" },
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--iio-border)" },
          "& .MuiSelect-select": { whiteSpace: "normal", overflowWrap: "anywhere" } }}>
          <InputLabel id="repository-pair-label">{t("repositoryDiscovery.pair")}</InputLabel>
          <Select labelId="repository-pair-label" label={t("repositoryDiscovery.pair")} value={choiceId} onChange={event => setChoiceId(event.target.value)}
            MenuProps={{ PaperProps: { className: "iio", sx: { background: "var(--iio-bg-1)", color: "var(--iio-text-1)" } } }}>
            {state.result.choices.map(choice => <MenuItem key={choice.id} value={choice.id} sx={{ fontFamily: "inherit", whiteSpace: "normal", overflowWrap: "anywhere" }}>{labelOf(choice)}</MenuItem>)}
          </Select>
        </FormControl>
        {state.result.choices.find(choice => choice.id === choiceId)?.contentBranch && <Typography sx={{ fontFamily: "inherit", mb: 2, overflowWrap: "anywhere" }}>
          {t("repositoryDiscovery.sourceBranch", { branch: state.result.choices.find(choice => choice.id === choiceId).contentBranch })}
        </Typography>}
        <Button variant="outlined" sx={buttonSx} disabled={!choiceId} onClick={confirm}>{t("repositoryDiscovery.confirm")}</Button>
      </>}
    </>}
    {state.error && <Button variant="outlined" sx={buttonSx}
      onClick={() => state.reloadRequired ? refs.current.onReload?.() : setAttempt(value => value + 1)}>
      {t(state.reloadRequired ? "repositoryDiscovery.reload" : "repositoryIdentity.retry")}</Button>}
  </Box>;
}
